import { Entity } from '@api/api/entity-schema';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { RankSection } from '@terminal/components/rank-section/RankSection';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { GeographicStepperSection } from '../geographic-stepper-section/GeographicStepperSection';
import { GeoSourceEvidenceSection } from '../source-evidence-section/GeoSourceEvidenceSection';
import { SourceEvidenceSectionRevamp } from '../source-evidence-section/SourceEvidenceSectionRevamp';
import styles from './GeoExposureDetails.module.scss';

export interface GeoExposureDetailsProps {
  entity: Entity;
  targetName: string;
  targetType: 'country' | 'region';
  title: string;
  rankTitle: string;
  description: string;
  rank: number;
  rankChange?: number | null;
  evidence?: string[] | null;
  sourceEvidence?: string | null;
  withDivider?: boolean;
}

export const loadingSkeletonAreas = [
  { styles: { width: '100%', height: '40px' } },
  { styles: { width: '100%', height: '100px' } },
];

export const GeoExposureDetails = ({
  entity,
  targetName,
  targetType,
  title,
  rankTitle,
  description,
  rank,
  rankChange,
  evidence,
  sourceEvidence,
  withDivider,
}: GeoExposureDetailsProps) => {
  const { t } = useTranslation(['widget', 'countries']);
  const isConnectionsDrawersOverhaul = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5359-connections-drawers-overhaul')
  );
  const companyName = entity.name_short || entity.name;
  const targetLabel =
    targetType === 'country'
      ? t(`countries:${targetName.toUpperCase()}`)
      : targetName;

  return (
    <div
      className={clsx(styles.root, {
        [styles.sections]: isConnectionsDrawersOverhaul,
      })}
    >
      <GeographicStepperSection
        entity={entity}
        targetName={targetName}
        targetType={targetType}
        title={title}
        activeIndex={0}
        description={description}
      />
      {isConnectionsDrawersOverhaul ? (
        <RankSection
          subjectName={companyName}
          targetName={targetLabel}
          rank={rank}
          rankChange={rankChange}
          description={description}
        />
      ) : (
        <div className={styles.sectionRow}>
          <div className={styles.titleBase}>{rankTitle}</div>
          <div className={styles.styledRankWrapper}>
            <div className={styles.rankContainer}>
              <div className={styles.rankLabel}>
                {t(
                  'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.current'
                )}
              </div>
              <div className={styles.rankFlexRow}>
                <TopRankIcon rank={rank} />
              </div>
            </div>
          </div>
        </div>
      )}
      <ErrorBoundaryWrapper>
        {isConnectionsDrawersOverhaul ? (
          <SourceEvidenceSectionRevamp
            evidence={evidence ?? []}
            sourceEvidence={sourceEvidence ?? null}
          />
        ) : (
          <GeoSourceEvidenceSection
            evidence={evidence ?? []}
            sourceEvidence={sourceEvidence ?? null}
          />
        )}
      </ErrorBoundaryWrapper>
      {withDivider ? <div className={styles.horizontalDivider} /> : null}
    </div>
  );
};
